// The synced things the web app makes, as the collections in sync.md §2.3 hold them, and the
// changes that create, edit and delete them. Free text (comments, note bodies, labels, shelf names)
// is encrypted at rest by the server; the server never holds Scripture or book text, only anchors.
import { keyedId, uuidv7 } from "./ids";
import type { Change } from "./ops";
import type { Anchor, LocalEntity } from "./types";

export type Highlight = { id: string; libraryId: string; anchor: Anchor; color: string; comment: string | null; createdAt: number };
export type Note = {
  id: string;
  libraryId: string | null;
  anchor: Anchor | null;
  title: string | null;
  /** Markdown. For a note the iOS app wrote in its own format, its plain text (see `format`). */
  body: string;
  tags: string[];
  createdAt: number;
  touched: number;
  /** "markdown", or "attributed-v1" for the iOS app's rich body: show `body` (its plain text), and treat it as read-only here so checklists and quote links aren't lost. */
  format: "markdown" | "attributed-v1";
  /** The folder (a `study` entity), FS-137. */
  studyId: string | null;
  /** What the note is about: a Bible book, chapter or range, or a work or its chapter. */
  scope: NoteScope | null;
  /** The passages it quotes, with the note's own copy of their words. */
  quotes: NoteQuote[];
  pinnedAt: number | null;
  /** Set while the note is in Recently Deleted on the app: hide it from lists. */
  deletedAt: number | null;
};

/** iOS NoteScope (FS-137); biblestandard-api docs/integration/sync.md §2.3. */
export type NoteScope = {
  kind: "bibleBook" | "bibleChapter" | "bibleRange" | "work" | "workChapter";
  title: string;
  book?: string;
  startChapter?: number;
  startVerse?: number;
  endChapter?: number;
  endVerse?: number;
  libraryId?: string;
  contentId?: number;
  sectionUid?: string;
};
/** iOS NoteAnchor: one quoted passage of Scripture (verseRange) or of a library work (libraryPassage). */
export type NoteQuote = {
  id: string;
  sourceKind: "verseRange" | "libraryPassage";
  book?: string;
  startChapter?: number;
  startVerse?: number;
  endChapter?: number;
  endVerse?: number;
  translationId?: string;
  libraryId?: string;
  contentId?: number;
  sectionUid?: string;
  blockKey?: string;
  location?: number;
  length?: number;
  textHash?: string;
  excerpt: string;
  citation: string;
  author?: string;
  workTitle?: string;
  chapterTitle?: string;
  createdAt?: number;
};
/** A folder of notes (iOS Study, FS-137 D7). */
export type Study = { id: string; title: string; parentId: string | null; sortIndex: number };
/** One plan reading marked read on a local day (iOS PlanReadMark, FS-197 D10). */
export type PlanReadMark = { id: string; planId: string; dayKey: string; targetKey: string; localDay: string; markedAt: number };
export type Bookmark = { id: string; libraryId: string; anchor: Anchor; label: string | null; createdAt: number };
export type ReadingPosition = { id: string; libraryId: string; anchor: Anchor | null; progress: number | null; readAt: number };
export type ShelfItem = { id: string; libraryId: string; collectionIds: string[]; hidden: boolean; addedAt: number; sortIndex: number | null };
export type ShelfCollection = { id: string; name: string; sortIndex: number | null; addedAt: number };

/** Highlight colours: quiet washes, the same names the app uses. Stored as #RRGGBB. */
export const HIGHLIGHT_COLORS = [
  { name: "Yellow", hex: "#F2D16B" },
  { name: "Green", hex: "#9CCB8A" },
  { name: "Blue", hex: "#8FB8DE" },
  { name: "Rose", hex: "#E8A0A6" },
] as const;

export const REFLECTION_TAG = "reflection";

const str = (v: unknown) => (typeof v === "string" ? v : null);
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const anchorOf = (v: unknown): Anchor | null => (v && typeof v === "object" ? (v as Anchor) : null);
const strings = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);

// ---- Reading entities back (a missing required field means "not yet known": skip it) ----

export function asHighlight(e: LocalEntity): Highlight | null {
  const libraryId = str(e.fields.libraryId);
  const anchor = anchorOf(e.fields.anchor);
  if (!libraryId || !anchor) return null;
  return { id: e.id, libraryId, anchor, color: str(e.fields.color) ?? HIGHLIGHT_COLORS[0].hex, comment: str(e.fields.comment), createdAt: num(e.fields.createdAt) ?? e.touched };
}

export function asNote(e: LocalEntity): Note {
  const format = e.fields.bodyFormat === "attributed-v1" ? "attributed-v1" : "markdown";
  return {
    id: e.id,
    libraryId: str(e.fields.libraryId),
    anchor: anchorOf(e.fields.anchor),
    title: str(e.fields.title),
    body: format === "markdown" ? (str(e.fields.body) ?? "") : (str(e.fields.plainText) ?? ""),
    tags: strings(e.fields.tags),
    createdAt: num(e.fields.createdAt) ?? e.touched,
    touched: e.touched,
    format,
    studyId: str(e.fields.studyId),
    scope: e.fields.scope && typeof e.fields.scope === "object" ? (e.fields.scope as NoteScope) : null,
    quotes: Array.isArray(e.fields.anchors) ? (e.fields.anchors as NoteQuote[]) : [],
    pinnedAt: num(e.fields.pinnedAt),
    deletedAt: num(e.fields.deletedAt),
  };
}

export function asStudy(e: LocalEntity): Study | null {
  const title = str(e.fields.title);
  if (title === null) return null;
  return { id: e.id, title, parentId: str(e.fields.parentId), sortIndex: num(e.fields.sortIndex) ?? 0 };
}

export function asPlanReadMark(e: LocalEntity): PlanReadMark | null {
  const planId = str(e.fields.planId);
  const localDay = str(e.fields.localDay);
  if (!planId || !localDay) return null;
  return { id: e.id, planId, dayKey: str(e.fields.dayKey) ?? "", targetKey: str(e.fields.targetKey) ?? "", localDay, markedAt: num(e.fields.markedAt) ?? e.touched };
}

/** A setting's value (the `setting` collection: `{key, value}`, keyed by key). */
export function asSetting(e: LocalEntity): { key: string; value: unknown } | null {
  const key = str(e.fields.key);
  return key ? { key, value: e.fields.value ?? null } : null;
}

export function asBookmark(e: LocalEntity): Bookmark | null {
  const libraryId = str(e.fields.libraryId);
  const anchor = anchorOf(e.fields.anchor);
  if (!libraryId || !anchor) return null;
  return { id: e.id, libraryId, anchor, label: str(e.fields.label), createdAt: num(e.fields.createdAt) ?? e.touched };
}

export function asPosition(e: LocalEntity): ReadingPosition | null {
  const libraryId = str(e.fields.libraryId);
  if (!libraryId) return null;
  return { id: e.id, libraryId, anchor: anchorOf(e.fields.anchor), progress: num(e.fields.progress), readAt: num(e.fields.readAt) ?? e.touched };
}

export function asShelfItem(e: LocalEntity): ShelfItem | null {
  const libraryId = str(e.fields.libraryId);
  if (e.fields.kind !== "item" || !libraryId) return null;
  return {
    id: e.id,
    libraryId,
    collectionIds: strings(e.fields.collectionIds),
    hidden: e.fields.hidden === true,
    addedAt: num(e.fields.addedAt) ?? e.touched,
    sortIndex: num(e.fields.sortIndex),
  };
}

export function asShelfCollection(e: LocalEntity): ShelfCollection | null {
  const name = str(e.fields.name);
  if (e.fields.kind !== "collection" || !name) return null;
  return { id: e.id, name, sortIndex: num(e.fields.sortIndex), addedAt: num(e.fields.addedAt) ?? e.touched };
}

// ---- Making changes ----

export function newHighlight(input: { libraryId: string; anchor: Anchor; color: string; comment?: string | null }, now = Date.now()): Change {
  return {
    collection: "highlight",
    id: uuidv7(now),
    op: "upsert",
    fields: { libraryId: input.libraryId, anchor: input.anchor, color: input.color, style: "fill", createdAt: now, ...(input.comment ? { comment: input.comment } : {}) },
  };
}

export function newNote(input: { libraryId?: string | null; anchor?: Anchor | null; title?: string | null; body: string; tags?: string[] }, now = Date.now()): Change {
  return {
    collection: "note",
    id: uuidv7(now),
    op: "upsert",
    fields: {
      ...(input.libraryId ? { libraryId: input.libraryId } : {}),
      anchor: input.anchor ?? null,
      ...(input.title ? { title: input.title } : {}),
      body: input.body,
      bodyFormat: "markdown",
      plainText: input.body,
      ...(input.tags?.length ? { tags: input.tags } : {}),
      createdAt: now,
      updatedAt: now,
    },
  };
}

export function newBookmark(input: { libraryId: string; anchor: Anchor; label?: string | null }, now = Date.now()): Change {
  return {
    collection: "bookmark",
    id: uuidv7(now),
    op: "upsert",
    fields: { libraryId: input.libraryId, anchor: input.anchor, ...(input.label ? { label: input.label } : {}), createdAt: now },
  };
}

export const remove = (collection: Change["collection"], id: string): Change => ({ collection, id, op: "delete" });
/**
 * An edit. A note's body written here is Markdown, so an edit that sets `body` also says so
 * (`bodyFormat`) and refreshes `plainText`: the iOS app reads either, and never mistakes Markdown for
 * its own rich format.
 */
export const edit = (collection: Change["collection"], id: string, fields: Record<string, unknown>): Change => ({
  collection,
  id,
  op: "upsert",
  fields:
    collection === "note" && typeof fields.body === "string" && !("bodyFormat" in fields)
      ? { ...fields, bodyFormat: "markdown", plainText: fields.body, updatedAt: Date.now() }
      : fields,
});

/** A reader setting (sync `setting`), e.g. the catechism they picked: one entity per key. */
export async function settingChange(key: string, value: unknown): Promise<Change> {
  return { collection: "setting", id: await keyedId("setting", key), op: "upsert", fields: { key, value } };
}

export async function positionChange(libraryId: string, anchor: Anchor, progress: number | null, now = Date.now()): Promise<Change> {
  return {
    collection: "readingPosition",
    id: await keyedId("readingPosition", libraryId),
    op: "upsert",
    fields: { libraryId, anchor, ...(progress === null ? {} : { progress: Math.min(1, Math.max(0, progress)) }), readAt: now },
  };
}

export async function shelfAdd(libraryId: string, now = Date.now()): Promise<Change> {
  return { collection: "shelf", id: await keyedId("shelf", libraryId), op: "upsert", fields: { kind: "item", libraryId, hidden: false, addedAt: now } };
}

export async function shelfRemove(libraryId: string): Promise<Change> {
  return { collection: "shelf", id: await keyedId("shelf", libraryId), op: "delete" };
}

export async function shelfFile(libraryId: string, collectionIds: string[]): Promise<Change> {
  return { collection: "shelf", id: await keyedId("shelf", libraryId), op: "upsert", fields: { kind: "item", libraryId, collectionIds: collectionIds.slice(0, 100) } };
}

export function newCollection(name: string, now = Date.now()): Change {
  return { collection: "shelf", id: uuidv7(now), op: "upsert", fields: { kind: "collection", name: name.trim().slice(0, 100), addedAt: now, sortIndex: now } };
}
